// DiscussButton — the "Discuss" affordance for a domain object (a work item, leave
// request, expense, …). It resolve-or-creates the conversation attached to the object
// and opens it in the GLOBAL slide-over, so the object's own screen stays mounted
// underneath and closing the panel returns to its exact state — the spec's "jump into
// its conversation and jump back without losing state". Entitlement-gated: renders
// nothing for a tenant without the conversations product.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { conversationsApi } from './api/conversationsApi';
import { useMessagesPanel } from './MessagesPanel';

interface Props {
  /** WORK_ITEM | ASSIGNMENT | LEAVE | EXPENSE | GOAL | RECOGNITION | MEETING | PROJECT */
  contextType: string;
  contextId: string;
  title?: string;
  label?: string;
  className?: string;
  /** Runs after the conversation is resolved and before it opens — e.g. the work
   * item asks its service to add the people on the item. Best-effort. */
  beforeOpen?: (conversationId: string) => Promise<unknown>;
}

export const DiscussButton: React.FC<Props> = ({ contextType, contextId, title, label, className, beforeOpen }) => {
  const { t } = useTranslation('conversations');
  const { hasProduct } = useEntitlements();
  const { open } = useMessagesPanel();
  const [busy, setBusy] = useState(false);

  if (!hasProduct('conversations') || !contextId) return null;

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const conv = await conversationsApi.createContextual(contextType, contextId, title);
      if (beforeOpen) {
        try {
          await beforeOpen(conv.id);
        } catch {
          /* the thread still opens; membership sync is best-effort */
        }
      }
      open(conv.id);
    } catch {
      /* best-effort — the object screen is unaffected */
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={
        className ??
        'inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-charcoal transition-colors hover:bg-muted disabled:opacity-60'
      }
    >
      <MessageSquare className="h-4 w-4" />
      {busy ? t('opening') : label ?? t('discuss')}
    </button>
  );
};

export default DiscussButton;
